// Baralho do Truco de 6 em SVG: as 40 cartas (sem 8, 9 e 10) e os versos. Origem: TrucoCards do protótipo.
import { SIMBOLO } from './marca.ts';
import { cores } from './tokens.ts';

const INK = '#15171C', RED = '#C8102E', PAPER = '#FBF8F1', EDGE = '#D9CFBC', GOLD = '#C9963A';
const FONT = "'Barlow Condensed','Arial Narrow',Arial,sans-serif";
// Manilha dourada: face paper-manilha, borda de 4 em manilha-edge e copas e ouros em suit-red-manilha (4,8:1 sobre o
// dourado). Paus e espadas seguem em INK. As outras cartas ficam iguais às do protótipo.
const MANILHA = { papel: cores.paperManilha, borda: cores.manilhaEdge, vermelho: cores.suitRedManilha };

export type Naipe = 'c' | 'h' | 's' | 'd';
export const NAIPES: Readonly<Record<Naipe, string>> = { c: 'paus', h: 'copas', s: 'espadas', d: 'ouros' };
export const POSTOS = ['4', '5', '6', '7', 'Q', 'J', 'K', 'A', '2', '3'] as const;

function color(s: Naipe, manilha = false): string { return s === 'h' || s === 'd' ? (manilha ? MANILHA.vermelho : RED) : INK; }

// Naipes desenhados numa caixa 100x100, centro (50,50)
function suitShape(s: Naipe, fill: string): string {
  if (s === 'h') return '<path fill="' + fill + '" d="M50 90C22 68 5 51 5 31 5 16 16 6 29 6c10 0 17 6 21 14 4-8 11-14 21-14 13 0 24 10 24 25 0 20-17 37-45 59z"/>';
  if (s === 'd') return '<path fill="' + fill + '" d="M50 3 89 50 50 97 11 50z"/>';
  if (s === 's') return '<path fill="' + fill + '" d="M50 4C63 24 94 38 94 61c0 14-11 23-23 23-9 0-15-4-18-10 1 9 5 16 12 22H35c7-6 11-13 12-22-3 6-9 10-18 10C17 84 6 75 6 61 6 38 37 24 50 4z"/>';
  return '<g fill="' + fill + '"><circle cx="50" cy="27" r="20"/><circle cx="26" cy="58" r="20"/><circle cx="74" cy="58" r="20"/><path d="M42 50h16l7 46H35z"/></g>';
}

function pip(s: Naipe, cx: number, cy: number, size: number, flip: boolean, manilha = false): string {
  const k = size / 100, tx = cx - size / 2, ty = cy - size / 2;
  const t = flip ? 'translate(' + (cx + size / 2) + ' ' + (cy + size / 2) + ') rotate(180) scale(' + k + ')'
    : 'translate(' + tx + ' ' + ty + ') scale(' + k + ')';
  return '<g transform="' + t + '">' + suitShape(s, color(s, manilha)) + '</g>';
}

// Posições normalizadas (0..1) dos naipes no miolo da carta
const LAYOUT: Record<string, [number, number][]> = {
  2: [[0.5, 0], [0.5, 1]],
  3: [[0.5, 0], [0.5, 0.5], [0.5, 1]],
  4: [[0, 0], [1, 0], [0, 1], [1, 1]],
  5: [[0, 0], [1, 0], [0.5, 0.5], [0, 1], [1, 1]],
  6: [[0, 0], [1, 0], [0, 0.5], [1, 0.5], [0, 1], [1, 1]],
  7: [[0, 0], [1, 0], [0.5, 0.25], [0, 0.5], [1, 0.5], [0, 1], [1, 1]],
};

function index(rank: string, s: Naipe, manilha: boolean): string {
  return '<text x="28" y="58" text-anchor="middle" font-family="' + FONT + '" font-weight="800" font-size="48" fill="' + color(s, manilha) + '">' + rank + '</text>' + pip(s, 28, 78, 24, false, manilha);
}

// O quadro creme do miolo fica também na manilha, sobre o dourado.
function court(rank: 'K' | 'Q' | 'J', s: Naipe, manilha: boolean): string {
  const c = color(s, manilha);
  const crown = {
    K: '<path d="M78 92l8-22 14 14 14-14 8 22z" fill="' + GOLD + '"/><circle cx="86" cy="68" r="4" fill="' + GOLD + '"/><circle cx="100" cy="80" r="4" fill="' + GOLD + '"/><circle cx="114" cy="68" r="4" fill="' + GOLD + '"/>',
    Q: '<path d="M80 92c4-14 12-20 20-20s16 6 20 20z" fill="' + GOLD + '"/><circle cx="100" cy="70" r="5" fill="' + GOLD + '"/>',
    J: '<path d="M80 92l4-16h32l4 16z" fill="' + GOLD + '"/><path d="M84 76l16-8 16 8z" fill="' + GOLD + '"/>',
  }[rank];
  return '<rect x="56" y="58" width="88" height="164" rx="10" fill="#F3E7CC" stroke="' + GOLD + '" stroke-width="3"/>' +
    crown +
    '<text x="100" y="164" text-anchor="middle" font-family="' + FONT + '" font-weight="800" font-size="78" fill="' + c + '">' + rank + '</text>' +
    pip(s, 100, 194, 30, false, manilha);
}

function face(rank: string, s: Naipe, manilha: boolean): string {
  if (rank === 'A') return pip(s, 100, 140, 96, false, manilha);
  if (rank === 'K' || rank === 'Q' || rank === 'J') return court(rank, s, manilha);
  const pts = LAYOUT[rank]!, x0 = 72, x1 = 128, y0 = 92, y1 = 188;
  let out = '';
  for (const p of pts) out += pip(s, x0 + (x1 - x0) * p[0], y0 + (y1 - y0) * p[1], 30, p[1] > 0.5, manilha);
  return out;
}

export function nomeDaCarta(rank: string, s: Naipe): string {
  const n = ({ A: 'Ás', K: 'Rei', J: 'Valete', Q: 'Dama' } as Record<string, string>)[rank] || rank;
  return n + ' de ' + NAIPES[s];
}

// manilha: a carta é manilha na mão em andamento (a seguinte à vira) e sai dourada.
export function cartaSVG(rank: string, s: Naipe, opts: { width?: number; manilha?: boolean } = {}): string {
  const w = opts.width || 200, h = Math.round(w * 1.4), m = !!opts.manilha;
  const fundo = m
    ? '<rect x="2" y="2" width="196" height="276" rx="16" fill="' + MANILHA.papel + '" stroke="' + MANILHA.borda + '" stroke-width="4"/>'
    : '<rect x="2" y="2" width="196" height="276" rx="16" fill="' + PAPER + '" stroke="' + EDGE + '" stroke-width="2"/>';
  const body = fundo + face(rank, s, m) + index(rank, s, m) +
    '<g transform="rotate(180 100 140)">' + index(rank, s, m) + '</g>';
  return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 280" width="' + w + '" height="' + h + '" role="img" aria-label="' + nomeDaCarta(rank, s) + (m ? ', manilha' : '') + '">' + body + '</svg>';
}

// padrao: o desenho do fundo (losango, o da casa; azulejo, listras ou pontos, dos cosméticos).
// mono: o monograma do centro (patrocinador e cosméticos). Sem ele, o centro é o 6 da marca: o verso da casa.
export interface OpcoesVerso {
  width?: number; base?: string; line?: string; mono?: string; monoFill?: string; monoInk?: string; padrao?: 'losango' | 'azulejo' | 'listras' | 'pontos';
}

function fundoDoVerso(padrao: OpcoesVerso['padrao'], line: string): string {
  let out = '';
  if (padrao === 'azulejo') {
    for (let x = 12; x < 200; x += 32) for (let y = 12; y < 280; y += 32) {
      out += '<rect x="' + x + '" y="' + y + '" width="32" height="32" fill="none" stroke="' + line + '" stroke-opacity=".45" stroke-width="2"/>' +
        '<circle cx="' + (x + 16) + '" cy="' + (y + 16) + '" r="9" fill="none" stroke="' + line + '" stroke-opacity=".6" stroke-width="3"/>' +
        '<circle cx="' + (x + 16) + '" cy="' + (y + 16) + '" r="2.5" fill="' + line + '" fill-opacity=".7"/>';
    }
  } else if (padrao === 'listras') {
    for (let x = 0; x < 200; x += 18) out += '<rect x="' + x + '" y="0" width="8" height="280" fill="' + line + '" fill-opacity=".28"/>';
  } else if (padrao === 'pontos') {
    for (let x = 18; x < 200; x += 20) for (let y = 18; y < 280; y += 20) out += '<circle cx="' + x + '" cy="' + y + '" r="3" fill="' + line + '" fill-opacity=".45"/>';
  } else {
    for (let i = -280; i < 480; i += 28) {
      out += '<path d="M' + i + ' 0l280 280M' + (i + 280) + ' 0l-280 280" stroke="' + line + '" stroke-opacity=".35" stroke-width="3"/>';
    }
  }
  return out;
}

// Centro do verso da casa (marca/verso/verso-casa.svg): o disco marca-fundo com o anel de papel, o filete dourado e o
// 6 laranja do símbolo com 72 px, centrado.
function seisDaCasa(line: string): string {
  return '<circle cx="100" cy="140" r="47" fill="' + cores.marcaFundo + '" stroke="#FBF8F1" stroke-width="5"/>' +
    '<circle cx="100" cy="140" r="40" fill="none" stroke="' + line + '" stroke-width="2"/>' +
    '<svg x="64" y="104" width="72" height="72" viewBox="' + SIMBOLO.viewBox + '"><path fill="' + cores.fire500 + '" fill-rule="evenodd" d="' + SIMBOLO.d + '"/></svg>';
}

// Verso: o da casa (verde e dourado, com o 6 da marca) ou um com monograma (patrocinador e cosméticos).
export function versoSVG(opts: OpcoesVerso = {}): string {
  const w = opts.width || 200, h = Math.round(w * 1.4);
  const base = opts.base || '#0E3B2C', line = opts.line || '#C9963A', mono = opts.mono;
  const monoFill = opts.monoFill || '#E8B547', monoInk = opts.monoInk || '#0E3B2C';
  const lattice = fundoDoVerso(opts.padrao, line);
  const centro = mono === undefined ? seisDaCasa(line)
    : '<circle cx="100" cy="140" r="44" fill="' + monoFill + '" stroke="#FBF8F1" stroke-width="5"/>' +
      '<text x="100" y="160" text-anchor="middle" font-family="' + FONT + '" font-weight="800" font-size="' + (mono.length > 2 ? 38 : 54) + '" fill="' + monoInk + '">' + mono + '</text>';
  const body = '<rect x="2" y="2" width="196" height="276" rx="16" fill="#FBF8F1"/>' +
    '<rect x="12" y="12" width="176" height="256" rx="10" fill="' + base + '"/>' +
    '<g clip-path="url(#k)">' + lattice + '</g>' +
    '<rect x="20" y="20" width="160" height="240" rx="6" fill="none" stroke="' + line + '" stroke-width="3"/>' + centro;
  const nome = mono === undefined ? 'Verso da carta, Truco de 6' : 'Verso da carta';
  return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 280" width="' + w + '" height="' + h + '" role="img" aria-label="' + nome + '"><defs><clipPath id="k"><rect x="12" y="12" width="176" height="256" rx="10"/></clipPath></defs>' + body + '</svg>';
}

// Sem patrocinador no lançamento: as cartas usam o verso da casa, com o 6. Um patrocinador passa as cores e o monograma dele.
export const VERSO_DA_CASA: OpcoesVerso = {};

// Carta pelo id do motor ('Ah', '4c') ou 'verso', como data URI para <img>. Guarda em cache, por id e por manilha.
// O verso nunca fica dourado.
const cache = new Map<string, string>();
export function imagemDaCarta(id: string, verso: OpcoesVerso = VERSO_DA_CASA, opts: { manilha?: boolean } = {}): string {
  const manilha = id !== 'verso' && !!opts.manilha;
  const chave = id === 'verso' ? 'verso:' + JSON.stringify(verso) : manilha ? id + ':manilha' : id;
  let uri = cache.get(chave);
  if (!uri) {
    const svg = id === 'verso' ? versoSVG(verso) : cartaSVG(id.slice(0, -1), id.slice(-1) as Naipe, { manilha });
    uri = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
    cache.set(chave, uri);
  }
  return uri;
}

// "6 de ouros, manilha": o leitor de tela diz que a carta é manilha.
export function nomeDoId(id: string, opts: { manilha?: boolean } = {}): string {
  return id === 'verso' ? 'Carta virada' : nomeDaCarta(id.slice(0, -1), id.slice(-1) as Naipe) + (opts.manilha ? ', manilha' : '');
}
